import { useEffect, useState } from 'react'
import { Check, ImagePlus, RefreshCw, Sparkles, Star } from 'lucide-react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../lib/shadcn/dialog'
import { Button } from '../../lib/shadcn/button'
import { Spinner } from '../../lib/shadcn/spinner'
import { toast } from '../../lib/shadcn/sonner'
import { cn } from '../../lib/shadcn/utils'
import { useCombineProductPhotos } from '../../hooks/backend/inventoryPage'

// Combine with AI: pick the bag photo and the loose popcorn photo, ChatGPT
// makes one photo of the bag with popcorn in front, and it's added to the
// product's photos once you like it. Nothing is saved to the product until
// the product dialog's Save.
export default function CombinePhotosDialog({
  open,
  onOpenChange,
  photos,
  onAdd,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  photos: string[]
  /** Adds the combined photo; featured puts it first. */
  onAdd: (url: string, featured: boolean) => void
}) {
  const combine = useCombineProductPhotos()
  const [bagUrl, setBagUrl] = useState<string | null>(null)
  const [pileUrl, setPileUrl] = useState<string | null>(null)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [working, setWorking] = useState(false)

  // Start with photo 1 as the bag and photo 2 as the popcorn, which is how
  // the products are set up.
  useEffect(() => {
    if (!open) return
    setBagUrl(photos[0] ?? null)
    setPileUrl(photos[1] ?? null)
    setResultUrl(null)
    setWorking(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const run = async () => {
    if (!bagUrl || !pileUrl) return
    setWorking(true)
    setResultUrl(null)
    try {
      const res = (await combine.trigger({ bagUrl, pileUrl }).result) as { url?: string } | undefined
      if (!res?.url) throw new Error('No photo came back')
      setResultUrl(res.url)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Combining photos failed')
    } finally {
      setWorking(false)
    }
  }

  const add = (featured: boolean) => {
    if (!resultUrl) return
    onAdd(resultUrl, featured)
    toast.success(
      featured
        ? 'Combined photo added as the featured photo. Click Save to keep it.'
        : 'Combined photo added. Click Save to keep it.'
    )
    onOpenChange(false)
  }

  const picker = (
    label: string,
    value: string | null,
    other: string | null,
    onPick: (url: string) => void
  ) => (
    <div className="space-y-1.5">
      <p className="text-sm font-medium">{label}</p>
      <div className="flex flex-wrap gap-2">
        {photos.map(url => {
          const selected = url === value
          const taken = url === other
          return (
            <button
              key={url}
              type="button"
              disabled={working || taken}
              onClick={() => onPick(url)}
              aria-label={selected ? `${label}: selected` : `Use as ${label.toLowerCase()}`}
              className={cn(
                'relative h-20 w-20 overflow-hidden rounded-md border bg-muted transition',
                selected ? 'ring-2 ring-primary' : 'opacity-70 hover:opacity-100',
                taken && 'cursor-not-allowed opacity-30 hover:opacity-30'
              )}
            >
              <img src={url} alt="" className="h-full w-full object-cover" />
              {selected && (
                <span className="absolute right-1 top-1 rounded-full bg-primary p-0.5 text-primary-foreground">
                  <Check className="h-3 w-3" />
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )

  return (
    <Dialog open={open} onOpenChange={o => !working && onOpenChange(o)}>
      <DialogContent className="max-h-[95vh] sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Combine with AI</DialogTitle>
          <DialogDescription>
            Pick the bag photo and the loose popcorn photo. ChatGPT puts some of the popcorn in front
            of the bag (about 35 seconds). It redraws the label, so check the text before adding it.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {picker('Bag', bagUrl, pileUrl, setBagUrl)}
            {picker('Popcorn', pileUrl, bagUrl, setPileUrl)}
          </div>

          {(working || resultUrl) && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_2fr]">
              <div className="hidden flex-col gap-2 sm:flex">
                {[bagUrl, pileUrl].map(
                  u =>
                    u && (
                      <img
                        key={u}
                        src={u}
                        alt=""
                        className="w-full rounded-md border bg-muted object-contain"
                      />
                    )
                )}
              </div>
              <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-md border bg-muted">
                {working ? (
                  <div className="flex flex-col items-center gap-2 text-sm text-muted-foreground">
                    <Spinner className="h-6 w-6" />
                    Combining…
                  </div>
                ) : (
                  resultUrl && (
                    <a href={resultUrl} target="_blank" rel="noreferrer" title="Open full size">
                      <img src={resultUrl} alt="Combined" className="h-full w-full object-contain" />
                    </a>
                  )
                )}
              </div>
            </div>
          )}
        </DialogBody>

        <DialogFooter className="gap-2">
          <Button variant="outline" disabled={working} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {resultUrl ? (
            <>
              <Button variant="outline" className="gap-1.5" disabled={working} onClick={() => void run()}>
                <RefreshCw className="h-4 w-4" />
                Try again
              </Button>
              <Button variant="outline" className="gap-1.5" disabled={working} onClick={() => add(true)}>
                <Star className="h-4 w-4" />
                Add as featured
              </Button>
              <Button className="gap-1.5" disabled={working} onClick={() => add(false)}>
                <ImagePlus className="h-4 w-4" />
                Add photo
              </Button>
            </>
          ) : (
            <Button
              className="gap-1.5"
              disabled={working || !bagUrl || !pileUrl}
              onClick={() => void run()}
            >
              {working ? <Spinner className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              {working ? 'Combining…' : 'Combine'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
